import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

// Add auth token to requests
client.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface TemplateSnapshot {
  id: string;
  templateId: string;
  templateName: string;
  snapshotHash: string;
  htmlContent: string;
  textContent: string;
  subject: string;
  imagePath?: string;  // Screenshot URL from MailerSend
  capturedAt: string;
  capturedBy: string;
  variables: string[];
  metadata?: {
    templateType?: string;
    category?: string;
  };
}

export interface TemplateSummary {
  templateId: string;
  templateName: string;
  latestSnapshotId: string;
  lastUpdated: string;
  versionCount: number;
}

export interface TemplatesResponse {
  success: boolean;
  data: TemplateSummary[];
  count: number;
}

export interface TemplateHistoryResponse {
  success: boolean;
  templateId: string;
  data: TemplateSnapshot[];
  count: number;
}

export interface SnapshotResponse {
  success: boolean;
  data: TemplateSnapshot;
}

export interface RefreshResponse {
  success: boolean;
  data: {
    total: number;
    refreshed: number;
    unchanged: number;
    errors: number;
    details: Array<{
      templateId: string;
      templateName: string;
      snapshotId: string;
      status: string;
    }>;
  };
}

// API Functions
export async function getTemplates(): Promise<TemplateSummary[]> {
  const response = await client.get<TemplatesResponse>('/ui/admin/templates');
  if (!response.data.success) {
    throw new Error('Failed to fetch templates');
  }
  return response.data.data;
}

export async function getTemplateHistory(templateId: string, limit = 50): Promise<TemplateSnapshot[]> {
  const response = await client.get<TemplateHistoryResponse>(
    `/ui/admin/templates/${templateId}/history`,
    { params: { limit } }
  );
  if (!response.data.success) {
    throw new Error('Failed to fetch template history');
  }
  return response.data.data;
}

export async function getSnapshot(snapshotId: string): Promise<TemplateSnapshot> {
  const response = await client.get<SnapshotResponse>(
    `/ui/admin/templates/snapshots/${snapshotId}`
  );
  if (!response.data.success) {
    throw new Error('Failed to fetch snapshot');
  }
  return response.data.data;
}

export function getSnapshotPreviewUrl(snapshotId: string): string {
  return `${API_BASE_URL}/ui/admin/templates/snapshots/${snapshotId}/preview`;
}

export async function refreshTemplates(): Promise<RefreshResponse['data']> {
  const response = await client.post<RefreshResponse>('/ui/admin/templates/refresh');
  if (!response.data.success) {
    throw new Error('Failed to refresh templates');
  }
  return response.data.data;
}

export async function createSnapshot(templateId: string): Promise<TemplateSnapshot> {
  const response = await client.post<SnapshotResponse>(
    `/ui/admin/templates/${templateId}/snapshot`
  );
  if (!response.data.success) {
    throw new Error('Failed to create snapshot');
  }
  return response.data.data;
}
